import type { HttpErrorResponse, HttpParams } from '@angular/common/http';
import { computed, inject } from '@angular/core';
import type { BaseApiClient, CrudSingleState } from '@indutrust/shared-model';
import { mapResponse } from '@ngrx/operators';
import {
  signalStoreFeature,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import type { EntityId } from '@ngrx/signals/entities';
import {
  Events,
  injectDispatch,
  on,
  withEventHandlers,
  withReducer,
} from '@ngrx/signals/events';
import { switchMap } from 'rxjs';
import { resolveHttpError } from '../../../http/resolve-http-error.util';
import {
  setCrudError,
  setCrudLoading,
  setCrudSuccess,
} from '../crud-store.util';
import type { createCrudSingleEvents } from './crud-single.events';

/**
 * Add event-driven CRUD state and handlers for managing a single entity in a Signal Store.
 *
 * This is the event-driven version that uses:
 * - `withReducer` to handle CRUD events and update state
 * - `withEventHandlers` to handle API calls (if service is provided)
 * - Methods to dispatch events
 *
 * Provides request status fields and convenience selectors (loading/success/error) for
 * loadById, edit and delete operations. Also provides convenience methods to dispatch these events.
 *
 * @template Entity The entity type which must have an `id` of type {@link EntityId}.
 * @template Service The service type that extends BaseApiClient<Entity>.
 * @param crudEvents The event group created by `createCrudSingleEvents<Entity>()`.
 * @param serviceFactory Optional factory function that injects the service. If provided,
 *                       API calls will be automatically handled. Pass a function like
 *                       `() => inject(YourService)` or `() => inject(YourService, { optional: true })`.
 * @returns A Signal Store feature that can be composed with other features.
 * @example
 * ```typescript
 * // With automatic API handling
 * const projectSingleEvents = createCrudSingleEvents<Project>('project.single');
 *
 * export const ProjectDetailStore = signalStore(
 *   { providedIn: 'root' },
 *   withCrudSingleEvents(projectSingleEvents, () => inject(ProjectService)),
 * );
 *
 * // Without API handling (handle in withEventHandlers yourself)
 * export const ProjectDetailStore = signalStore(
 *   { providedIn: 'root' },
 *   withCrudSingleEvents(projectSingleEvents),
 *   withEventHandlers((store, events, service) => ({
 *     // custom handlers
 *   }))
 * );
 * ```
 */
export function withCrudSingleEvents<Entity extends { id: EntityId }>(
  crudEvents: ReturnType<typeof createCrudSingleEvents<Entity>>,
  serviceFactory?: () => BaseApiClient<Entity>
) {
  const baseFeatures = signalStoreFeature(
    withState<CrudSingleState<Entity>>({
      entity: null,
      loadByIdStatus: 'initial',
      editStatus: 'initial',
      deleteStatus: 'initial',
    }),
    withComputed(({ loadByIdStatus, editStatus, deleteStatus }) => ({
      loadByIdLoading: computed(() => loadByIdStatus() === 'loading'),
      loadByIdSuccess: computed(() => loadByIdStatus() === 'success'),
      loadByIdError: computed(() => {
        const status = loadByIdStatus();
        return typeof status === 'object' ? status.error : null;
      }),
      editLoading: computed(() => editStatus() === 'loading'),
      editSuccess: computed(() => editStatus() === 'success'),
      editError: computed(() => {
        const status = editStatus();
        return typeof status === 'object' ? status.error : null;
      }),
      deleteLoading: computed(() => deleteStatus() === 'loading'),
      deleteSuccess: computed(() => deleteStatus() === 'success'),
      deleteError: computed(() => {
        const status = deleteStatus();
        return typeof status === 'object' ? status.error : null;
      }),
    })),
    withComputed(({ loadByIdLoading, editLoading, deleteLoading }) => ({
      anyLoading: computed(
        () => loadByIdLoading() || editLoading() || deleteLoading()
      ),
    })),
    // Reducer to handle all CRUD events
    withReducer(
      // LoadById events
      on(crudEvents.loadByIdRequested, () => setCrudLoading('loadById')),
      on(crudEvents.loadByIdSucceeded, ({ payload: entity }) => ({
        ...setCrudSuccess('loadById'),
        entity,
      })),
      on(crudEvents.loadByIdFailed, ({ payload: errorMessage }) =>
        setCrudError('loadById', errorMessage)
      ),

      // Edit events
      on(crudEvents.editRequested, () => setCrudLoading('edit')),
      on(crudEvents.editSucceeded, ({ payload: entity }) => ({
        ...setCrudSuccess('edit'),
        entity,
      })),
      on(crudEvents.editFailed, ({ payload: errorMessage }) =>
        setCrudError('edit', errorMessage)
      ),

      // Delete events
      on(crudEvents.deleteRequested, () => setCrudLoading('delete')),
      on(crudEvents.deleteSucceeded, () => ({
        ...setCrudSuccess('delete'),
        entity: null,
      })),
      on(crudEvents.deleteFailed, ({ payload: errorMessage }) =>
        setCrudError('delete', errorMessage)
      )
    ),
    withMethods(() => {
      const dispatch = injectDispatch(crudEvents);
      return {
        /**
         * Dispatch loadById event
         */
        loadById(
          id: EntityId,
          urlSegments?: Record<string, string | number>,
          params?: HttpParams
        ): void {
          dispatch.loadByIdRequested({ id, urlSegments, params });
        },
        /**
         * Dispatch edit event
         */
        edit(
          id: EntityId,
          payload: Partial<Entity>,
          urlSegments?: Record<string, string | number>,
          params?: HttpParams
        ): void {
          dispatch.editRequested({ id, payload, urlSegments, params });
        },
        /**
         * Dispatch delete event
         */
        delete(
          id: EntityId,
          urlSegments?: Record<string, string | number>,
          params?: HttpParams
        ): void {
          dispatch.deleteRequested({ id, urlSegments, params });
        },
      };
    })
  );

  // If service factory is provided, add event handlers
  if (serviceFactory) {
    return signalStoreFeature(
      baseFeatures,
      withEventHandlers(
        (store, events = inject(Events), service = serviceFactory()) => {
          return {
            /**
             * Handles entity loadById requests.
             * Fetches a single entity from the API and dispatches success/failure events.
             */
            loadByIdEntity$: events.on(crudEvents.loadByIdRequested).pipe(
              switchMap(({ payload }) =>
                service.getById(payload.id).pipe(
                  mapResponse({
                    next: (entity) => crudEvents.loadByIdSucceeded(entity),
                    error: (error: HttpErrorResponse) =>
                      crudEvents.loadByIdFailed(resolveHttpError(error).code),
                  })
                )
              )
            ),
            /**
             * Handles entity edit requests.
             * Updates an entity via the API and dispatches success/failure events.
             */
            editEntity$: events.on(crudEvents.editRequested).pipe(
              switchMap(({ payload }) =>
                service.patch(payload.id, payload.payload).pipe(
                  mapResponse({
                    next: (entity) => crudEvents.editSucceeded(entity),
                    error: (error: HttpErrorResponse) =>
                      crudEvents.editFailed(resolveHttpError(error).code),
                  })
                )
              )
            ),
            /**
             * Handles entity delete requests.
             * Deletes an entity via the API and dispatches success/failure events.
             */
            deleteEntity$: events.on(crudEvents.deleteRequested).pipe(
              switchMap(({ payload }) =>
                service.delete(payload.id).pipe(
                  mapResponse({
                    next: () => crudEvents.deleteSucceeded(payload.id),
                    error: (error: HttpErrorResponse) =>
                      crudEvents.deleteFailed(resolveHttpError(error).code),
                  })
                )
              )
            ),
          };
        }
      )
    );
  }

  return baseFeatures;
}
